"use client";

import { c as _c } from "react/compiler-runtime";
import { COIN_PAIRS } from "@pools/consts/coins";
import { useEffect } from "react";
import { useDebugSettings } from "@/providers/debug-settings";
import { PoolsFiltersProvider, usePoolsFilters } from "../hooks";
import { type PoolFilters } from "../store/poolsStore";
import { PoolsFilters, PoolsPairs, PoolsTableTanstack } from "./table";
const generatePageTitle = (filters: PoolFilters, selectedCoinPair: {
  pairs: [{
    symbol: string;
  }, {
    symbol: string;
  }];
} | null, includeVolTvl: boolean): string => {
  const parts: string[] = [];

  // Coin pair takes priority
  if (selectedCoinPair) {
    const [first, second] = selectedCoinPair.pairs;
    parts.push(`${first.symbol}/${second.symbol}`);
  }

  // Keyword is next priority (but only if no coin pair or it's short)
  if (filters.keyword && (!selectedCoinPair || filters.keyword.length <= 20)) {
    parts.push(filters.keyword);
  }

  // Then protocol
  if (filters.protocol !== "all") {
    parts.push(filters.protocol);
  }

  // Then network
  if (filters.network.length > 0) {
    const names = filters.network.length > 2 ? `${filters.network.slice(0, 2).join(", ")} +${filters.network.length - 2}` : filters.network.join(", ");
    parts.push(names);
  }

  // Other filters if they're set
  if (filters.feeTier !== "all") {
    parts.push(filters.feeTier);
  }
  if (filters.tvl !== "all") {
    const tvlLabels: Record<string, string> = {
      "10k": "> $10K TVL",
      "100k": "> $100K TVL",
      "1m": "> $1M TVL"
    };
    parts.push(tvlLabels[filters.tvl] || filters.tvl);
  }
  if (filters["1dVolume"] !== "all") {
    const volumeLabels: Record<string, string> = {
      "1k": "> $1K 1D vol",
      "10k": "> $10K 1D vol",
      "100k": "> $100K 1D vol"
    };
    parts.push(volumeLabels[filters["1dVolume"]] ?? filters["1dVolume"]);
  }
  if (includeVolTvl && filters.volTvl !== "all") {
    const volTvlLabels: Record<string, string> = {
      "0.05": "> 0.05 Vol/TVL",
      "0.2": "> 0.20 Vol/TVL",
      "0.5": "> 0.50 Vol/TVL",
      "1": "> 1.00 Vol/TVL"
    };
    parts.push(volTvlLabels[filters.volTvl] ?? filters.volTvl);
  }
  if (filters.marketCap !== "all") {
    const marketCapLabels: Record<string, string> = {
      top_50: "Top 50",
      top_100: "Top 100",
      top_200: "Top 200"
    };
    parts.push(marketCapLabels[filters.marketCap] ?? filters.marketCap);
  }

  // Build title: "Unilytics | Pools | [filters...]" or "Unilytics | Pools" if no filters
  const filterPart = parts.length > 0 ? ` | ${parts.join(" | ")}` : "";
  return `Unilytics | Pools${filterPart}`;
};
function LiquidityPoolsContent() {
  const $ = _c(8);
  const {
    filters,
    selectedCoinPair
  } = usePoolsFilters();
  const {
    volTvlEnabled
  } = useDebugSettings();
  let t0;
  let t1;
  if ($[0] !== filters || $[1] !== selectedCoinPair || $[2] !== volTvlEnabled) {
    t0 = () => {
      document.title = generatePageTitle(filters, selectedCoinPair, volTvlEnabled);
    };
    t1 = [filters, selectedCoinPair, volTvlEnabled];
    $[0] = filters;
    $[1] = selectedCoinPair;
    $[2] = volTvlEnabled;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="mb-4 font-semibold text-text-primary">Pair Type</h2>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="hidden border-b border-border-primary py-6 lg:block">{t2}<div className="grid w-full grid-cols-6 gap-3 overflow-x-auto"><PoolsPairs pairs={COIN_PAIRS} /></div></div>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="page-shell font-body">{t3}<div className="py-6"><PoolsFilters /><PoolsTableTanstack /></div></div>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
export default function LiquidityPools() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PoolsFiltersProvider><LiquidityPoolsContent /></PoolsFiltersProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// Export PoolFilters type for backwards compatibility
export type { PoolFilters };